import { useState } from 'react';
import { useTeamMembers, useTeamMembership } from '@/api/teams';
import type { Team, User } from '@/api/types';
import { Avatar, Button, EmptyState, ErrorState, Modal, Select, Spinner, useToast } from '@/components/ui';
import { RoleBadge } from './shared';

export function TeamMembersModal({
  team,
  allUsers,
  canManage,
  onClose,
}: {
  team: Team;
  allUsers: User[];
  canManage: boolean;
  onClose: () => void;
}) {
  const members = useTeamMembers(team.id);
  const membership = useTeamMembership();
  const toast = useToast();
  const [toAdd, setToAdd] = useState('');
  const memberIds = new Set((members.data ?? []).map((m) => m.id));
  const candidates = allUsers.filter((u) => !memberIds.has(u.id));

  const change = (userId: string, action: 'add' | 'remove') =>
    membership.mutate(
      { teamId: team.id, userId, action },
      { onSuccess: () => action === 'add' && setToAdd(''), onError: (e) => toast.error(e.message) },
    );

  return (
    <Modal
      open
      onClose={onClose}
      title={`${team.name} members`}
      description="Members receive this team’s tickets and chats."
    >
      {canManage && (
        <div className="mb-4 flex items-end gap-2">
          <Select
            label="Add member"
            value={toAdd}
            onChange={(e) => setToAdd(e.target.value)}
            wrapperClassName="flex-1"
          >
            <option value="">Choose a person…</option>
            {candidates.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name} — {u.email}
              </option>
            ))}
          </Select>
          <Button
            disabled={!toAdd}
            loading={membership.isPending && !!toAdd}
            onClick={() => change(toAdd, 'add')}
          >
            Add
          </Button>
        </div>
      )}
      {members.isPending ? (
        <div className="flex justify-center py-8">
          <Spinner label="Loading members" />
        </div>
      ) : members.error ? (
        <ErrorState error={members.error} onRetry={() => void members.refetch()} />
      ) : members.data.length === 0 ? (
        <EmptyState title="No members yet" description="Add agents so this team can receive work." />
      ) : (
        <ul className="divide-y divide-slate-100">
          {members.data.map((u) => (
            <li key={u.id} className="flex items-center gap-3 py-2.5">
              <Avatar name={u.name} status={u.status} size="sm" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-slate-900">{u.name}</p>
                <p className="truncate text-xs text-slate-500">{u.skills.join(', ') || 'No skills'}</p>
              </div>
              <RoleBadge role={u.role} />
              {canManage && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => change(u.id, 'remove')}
                  aria-label={`Remove ${u.name}`}
                >
                  Remove
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}
